import {
  ERROR_MAP,
  MEDIA_PROGRESS_STAGE,
  MEDIA_SOURCE,
  MEDIA_TYPE,
  TEST,
  TEST_DATA_URL,
} from '../constants';
import { isNil, isNotNil } from '../util/object';
import { warn } from '../util/log';
import { createSelector } from 'reselect';
import Medium from '../model/Medium';
import { getMediaUrlByMedia } from '../util/mediaUtils';
import { isEmpty, isNotEmpty } from '../util/array';
import { createKeyedStateSelector } from '../features/shared/utils/selectorUtils';

export const getAllIds = (state) => state.media.media;
export const getAllMediaById = (state) => state.media.mediaById;

// @todo(hamik): move into another file? https://jira.sc-corp.net/browse/ADS-37067
export const getByIdHelper = (allMediaById, mediaId) => {
  if (isNil(mediaId)) return null;
  const media = allMediaById[mediaId];
  if (!media) {
    warn(`No media found with id ${mediaId}`);
    return null;
  }
  return media;
};

export const getById = createKeyedStateSelector(
  [getAllMediaById, (state, mediaId) => mediaId],
  (allMediaById, mediaId) => getByIdHelper(allMediaById, mediaId),
);

export const getMediumById = createKeyedStateSelector([getById], (media) => {
  if (isNil(media)) return null;
  return new Medium(media);
});

export const getProgressById = createKeyedStateSelector([getById], (media) => {
  if (!media) {
    return null;
  }

  let stage;
  let progress = 0;

  if (!media.uploadComplete && media.uploaded) {
    stage = MEDIA_PROGRESS_STAGE.PROCESSING;
    progress = media.uploadedPercent;
  } else if (!media.uploaded) {
    stage = MEDIA_PROGRESS_STAGE.UPLOADING;
    progress = media.uploadedPercent;
  } else {
    stage = MEDIA_PROGRESS_STAGE.DONE;
    progress = 0;
  }

  return {
    stage,
    progress,
  };
});

export const getMediaInCreative = createSelector(
  [getAllIds, getAllMediaById],
  (allMediaIds, mediaMap) => {
    return allMediaIds.map((mediaId) => mediaMap[mediaId]);
  },
);

export const getAllMedia = createSelector(
  [getMediaInCreative, (state, mediaType) => mediaType],
  (allMediaInCreative, mediaType = null) => {
    if (isNil(mediaType)) return allMediaInCreative;

    return allMediaInCreative.filter((media) => mediaType === media.mediaType);
  },
);

/**
 * Finds the list uploading media by selecting all of the elements in the canvas and filtering down
 *   to the media objects that are still uploading or not completely handled by the server.
 *   This selector does not care about media that is uploading but not referenced in the canvas by an element.
 * @param  {Object} state - redux state
 * @return {Array}        - array of media objects that are still uploading
 */
export const getUploadingMedia = createSelector([getAllMedia], (allMedia) => {
  return allMedia.filter(
    (x) =>
      (x.mediaType !== MEDIA_TYPE.INLINE_SVG && !x.serverId) ||
      !x.uploaded ||
      !x.loaded ||
      !x.uploadComplete,
  );
});

export const getMediaByServerId = createKeyedStateSelector(
  [getMediaInCreative, (state, mediaServerId) => mediaServerId],
  (mediaInCreative, mediaServerId) => {
    return mediaInCreative.filter(
      (medium) => medium.serverId === mediaServerId,
    );
  },
);

export const getUrlById = createKeyedStateSelector([getById], (media) => {
  if (!media) {
    return null;
  }

  // If in TEST mode, return a data URL
  if (TEST) {
    return TEST_DATA_URL;
  }

  return getMediaUrlByMedia(media);
});

/**
 * Determines if there is media refrenced in the canvas that is still uploading.
 * @param  {Object} state - redux state
 * @return {Boolean}      - whether or not all of the referenced media is still uploading
 */
export const areAllReady = (state) => !getUploadingMedia(state).length;

export const isAudibleType = createKeyedStateSelector([getById], (media) => {
  const { mediaType } = media;
  return mediaType === MEDIA_TYPE.AUDIO || mediaType === MEDIA_TYPE.VIDEO;
});

export const hasAudio = (media) => {
  if (isNil(media)) return false;
  const { mediaType, audioServerId } = media;

  return (
    mediaType === MEDIA_TYPE.AUDIO ||
    (mediaType === MEDIA_TYPE.VIDEO && audioServerId !== null)
  );
};

export const dimensionsSet = (media) => {
  return (
    isNotNil(media) &&
    isNotNil(media.width) &&
    media.width > 0 &&
    isNotNil(media.height) &&
    media.height > 0
  );
};

export const durationSet = (media) => {
  return isNotNil(media) && isNotNil(media.duration);
};

export const hasFrameFormatSet = (media) => {
  if (isNil(media)) return false;
  const { mediaType, frameFormat } = media;
  if (mediaType !== MEDIA_TYPE.VIDEO) return true;
  return isNotNil(frameFormat);
};

export const hasVariantsSet = (media) => {
  if (isNil(media)) return false;
  const { mediaType, variants } = media;
  if (mediaType !== MEDIA_TYPE.VIDEO) return true;
  return isNotEmpty(variants);
};

export const hasAudioResponseSet = (media) => {
  const { mediaType, processedAudio } = media;
  const canHaveAudio =
    mediaType === MEDIA_TYPE.VIDEO || mediaType === MEDIA_TYPE.AUDIO;
  if (!canHaveAudio) {
    return true;
  }
  return !!processedAudio;
};

export const isUploadComplete = (media) => {
  return media.uploadComplete;
};

export const hasCenterX = (media) => {
  return isNotNil(media) && isNotNil(media.centerX);
};

export const getWidth = (state, mediaId) => {
  const media = getById(state, mediaId);
  if (!media) {
    return null;
  }

  return media.width;
};

export const getHeight = (state, mediaId) => {
  const media = getById(state, mediaId);
  if (!media) {
    return null;
  }

  return media.height;
};

export const getCenterX = (state, mediaId) => {
  const media = getById(state, mediaId);
  if (!media) {
    return null;
  }

  return media.centerX;
};

export const hasBeenAnalysed = (media) => {
  if (!media || !media.loaded) return false;

  if (media.mediaType === MEDIA_TYPE.IMAGE) {
    return hasCenterX(media);
  }

  return true;
};

export const hasVISErrors = (media) => {
  if (!media) return false;

  const { errors } = media;
  if (isEmpty(errors)) return false;

  return errors.find((error) => {
    const mediaError = JSON.parse(error);
    return mediaError.id === ERROR_MAP.VIS_PROCESSING_ERROR.id;
  });
};

export const isAnalysisInProgress = (media) => {
  return media && !hasBeenAnalysed(media) && media.isAnalysisInProgress;
};

export const getMediaTypesInUse = (state) => {
  const mediaTypesInUse = new Set();
  getAllMedia(state).forEach((media) => {
    if (isNil(media) || isNil(media.mediaType)) return;
    mediaTypesInUse.add(media.mediaType);
  });
  return Array.from(mediaTypesInUse.keys());
};

export const getDuration = createKeyedStateSelector([getById], (media) => {
  if (isNil(media)) return null;
  return media.duration;
});

export const creativeHasRestrictedMedia = createSelector(
  [getAllMedia],
  (allMediaById) => {
    if (isEmpty(allMediaById)) return false;

    return allMediaById.some(({ source }) => {
      return (
        source === MEDIA_SOURCE.STORY_KIT ||
        source === MEDIA_SOURCE.STORY_KIT_OUR_STORIES
      );
    });
  },
);

export const getCuratedMedia = createSelector([getAllMedia], (allMediaById) => {
  if (isEmpty(allMediaById)) return false;

  return allMediaById.filter(({ source }) => {
    return (
      source === MEDIA_SOURCE.STORY_KIT ||
      source === MEDIA_SOURCE.STORY_KIT_OUR_STORIES
    );
  });
});
